/**
 * Single source of truth for the "what-if" storm-intensity scaling
 * applied client-side to RAINFALL_VECTOR features.
 *
 * Background: the rainfall worker emits per-feature storm-derived
 * metrics (`inflowLs`, `peakRunoffLs`, `fillTimeS`, `totalRunoffM3`,
 * `storm.intensityMmPerHr`, `storm.depthMm`) computed from the design
 * storm the user picked at run time. The Rational-method scaling
 * (Q = C × i × A) is linear in `i`, so we can answer the
 * drain-consultant question — "what if rainfall is worse than design?" —
 * without re-running the worker, by multiplying / dividing the same
 * stored values.
 *
 * Mapping:
 *   - inflowLs        ∝ scale
 *   - peakRunoffLs    ∝ scale
 *   - totalRunoffM3   ∝ scale       (depth × area × C, depth scales)
 *   - intensityMmPerHr ∝ scale
 *   - depthMm         ∝ scale
 *   - fillTimeS       ∝ 1 / scale   (more rain → faster fill)
 *
 * Pass-through (unaffected by scale): pond geometry, volumes, areas,
 * flow paths, chain structure, the DEM. The user's scenario sliders
 * never change the catchment, only how hard it rains.
 *
 * Every consumer of storm-mode fields MUST go through this helper —
 * no bespoke `× scale` multiplications scattered across components.
 * That keeps the scaling rule auditable and prevents drift between
 * the inspector, leaderboard, fill animations, and CSV export.
 */

const isNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/**
 * What gets returned to a pond inspector / leaderboard row after
 * applying the storm scale. Optional fields are coerced to `null`
 * when missing so consumers can branch on `value == null` once
 * (instead of also handling `undefined`).
 */
export interface ScaledPondMetrics {
  /** Scaled inflow rate (L/s). null when worker didn't emit one (TOPOLOGY runs). */
  inflowLs: number | null;
  /** Scaled time-to-overflow (s). null when worker didn't emit one. */
  fillTimeS: number | null;
  /** Scaled total runoff for the catchment (m³). */
  totalRunoffM3: number | null;
  /** Pass-through (unaffected by scale). */
  volumeM3: number | null;
  /** Pass-through (unaffected by scale). */
  surfaceAreaM2: number | null;
  /** Pass-through (unaffected by scale). */
  maxDepthM: number | null;
  /** Pass-through (unaffected by scale). */
  catchmentAreaM2: number | null;
  /** Derived: volumeM3 / scaledTotalRunoffM3, clamped to [0,∞). null if either side is missing. */
  captureRatio: number | null;
  /** Derived: max(0, scaledTotalRunoffM3 - volumeM3). */
  spilloverM3: number | null;
}

/**
 * Apply the current what-if scale to the storm-derived numeric
 * properties on a pond GeoJSON feature. Pass-through fields (geometry,
 * volume, areas) are returned alongside so the caller can render a
 * single block of metrics from one source.
 */
export function scaledPondMetrics(
  props: Record<string, unknown> | null | undefined,
  scale: number,
): ScaledPondMetrics {
  const safeScale = isNumber(scale) && scale > 0 ? scale : 1;
  const p = props ?? {};

  const inflowLs = isNumber(p.inflowLs) ? p.inflowLs * safeScale : null;
  // fillTimeS scales inversely — a 2× storm fills the pond in half the
  // time. Non-positive fill times don't make physical sense; coerce to
  // null rather than divide-by-zero.
  const fillTimeS =
    isNumber(p.fillTimeS) && p.fillTimeS > 0 ? p.fillTimeS / safeScale : null;
  const totalRunoffM3 = isNumber(p.totalRunoffM3) ? p.totalRunoffM3 * safeScale : null;

  const volumeM3 = isNumber(p.volumeM3) ? p.volumeM3 : null;
  const surfaceAreaM2 = isNumber(p.surfaceAreaM2) ? p.surfaceAreaM2 : null;
  const maxDepthM = isNumber(p.maxDepthM) ? p.maxDepthM : null;
  const catchmentAreaM2 = isNumber(p.catchmentAreaM2) ? p.catchmentAreaM2 : null;

  const captureRatio =
    volumeM3 != null && totalRunoffM3 != null && totalRunoffM3 > 0
      ? volumeM3 / totalRunoffM3
      : null;
  const spilloverM3 =
    volumeM3 != null && totalRunoffM3 != null
      ? Math.max(0, totalRunoffM3 - volumeM3)
      : null;

  return {
    inflowLs,
    fillTimeS,
    totalRunoffM3,
    volumeM3,
    surfaceAreaM2,
    maxDepthM,
    catchmentAreaM2,
    captureRatio,
    spilloverM3,
  };
}

/** Scale a flow-line peak runoff (L/s). null in / null out. */
export function scaledFlowLineRunoffLs(
  peakRunoffLs: number | null | undefined,
  scale: number,
): number | null {
  if (!isNumber(peakRunoffLs)) return null;
  const safeScale = isNumber(scale) && scale > 0 ? scale : 1;
  return peakRunoffLs * safeScale;
}

/** Scale a storm intensity (mm/h). null in / null out. */
export function scaledStormIntensity(
  intensityMmPerHr: number | null | undefined,
  scale: number,
): number | null {
  if (!isNumber(intensityMmPerHr)) return null;
  const safeScale = isNumber(scale) && scale > 0 ? scale : 1;
  return intensityMmPerHr * safeScale;
}

/** Scale a storm rainfall depth (mm). null in / null out. */
export function scaledStormDepth(
  depthMm: number | null | undefined,
  scale: number,
): number | null {
  if (!isNumber(depthMm)) return null;
  const safeScale = isNumber(scale) && scale > 0 ? scale : 1;
  return depthMm * safeScale;
}

/** Scale an aggregate total runoff (m³). null in / null out. */
export function scaledTotalRunoffM3(
  totalRunoffM3: number | null | undefined,
  scale: number,
): number | null {
  if (!isNumber(totalRunoffM3)) return null;
  const safeScale = isNumber(scale) && scale > 0 ? scale : 1;
  return totalRunoffM3 * safeScale;
}

/** Scale a single fillTimeS scalar (s). null in / null out. */
export function scaledFillTimeS(
  fillTimeS: number | null | undefined,
  scale: number,
): number | null {
  if (!isNumber(fillTimeS) || fillTimeS <= 0) return null;
  const safeScale = isNumber(scale) && scale > 0 ? scale : 1;
  return fillTimeS / safeScale;
}

/** Slider bounds — lifted to module-level so UI + helper agree. */
export const RAINFALL_SCALE_MIN = 0.5;
export const RAINFALL_SCALE_MAX = 3.0;
export const RAINFALL_SCALE_DEFAULT = 1.0;

/** Snap-to-1× tolerance (slider-step rounding). 0.05 = 5 % around 1×. */
export const RAINFALL_SCALE_SNAP_EPSILON = 0.03;

/**
 * Format a multiplier for display. 1.5 → "1.50", 0.5 → "0.50". Locale-
 * agnostic on purpose: drainage engineers expect a dot decimal and no
 * thousands separators, even on de/fr locales.
 */
export function formatScale(scale: number): string {
  if (!isNumber(scale)) return '1.00';
  return scale.toFixed(2);
}
